// packages/web/src/components/settings/TelegramConnection.tsx
import React, { useState, useEffect } from 'react';
import { Send, AlertTriangle, CheckCircle, XCircle, Copy, ExternalLink } from 'lucide-react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import telegramService from '../../services/telegramService';

interface TelegramConnectionProps {
  onComplete?: () => void;
}

const TelegramConnection: React.FC<TelegramConnectionProps> = ({ onComplete }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const { setShowUpgradeModal } = useSubscription();

  useEffect(() => {
    checkConnectionStatus();
  }, []);

  const checkConnectionStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setShowUpgrade(false);
      
      const status = await telegramService.getConnectionStatus();
      setIsConnected(status.isConnected);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setShowUpgrade(true);
      } else {
        setError('Failed to check connection status');
        console.error(err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const generatePairingCode = async () => {
    try {
      setIsGeneratingCode(true);
      setError(null);
      setCodeCopied(false);
      
      const response = await fetch('/api/telegram/pairing-code', {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate pairing code');
      }
      
      const data = await response.json();
      setPairingCode(data.code);
      
      // Set up a timer to check connection status periodically
      const checkInterval = setInterval(async () => {
        try {
          const statusResponse = await fetch('/api/telegram/status', {
            credentials: 'include'
          });
          
          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            
            if (statusData.isConnected) {
              clearInterval(checkInterval);
              setIsConnected(true);
              setPairingCode(null);
              if (onComplete) onComplete();
            }
          }
        } catch (err) {
          console.error('Error checking connection status:', err);
        }
      }, 5000); // Check every 5 seconds
      
      // Clear interval after 15 minutes (maximum code validity)
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!isConnected) {
          setPairingCode(null);
        }
      }, 15 * 60 * 1000);
    } catch (err) {
      setError('Failed to generate pairing code');
      console.error(err);
    } finally {
      setIsGeneratingCode(false);
    }
  };

  const disconnectTelegram = async () => {
    if (!window.confirm('Are you sure you want to disconnect Telegram? You will no longer be able to receive or send messages via Telegram.')) {
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch('/api/telegram/disconnect', {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to disconnect Telegram');
      }
      
      setIsConnected(false);
    } catch (err) {
      setError('Failed to disconnect Telegram');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const copyPairingCode = () => {
    if (pairingCode) {
      navigator.clipboard.writeText(pairingCode).then(() => {
        setCodeCopied(true);
        setTimeout(() => setCodeCopied(false), 2000);
      }).catch(err => {
        console.error('Error copying to clipboard:', err);
      });
    }
  };

  const openTelegramBot = () => {
    window.open('https://t.me/icCRMBot', '_blank');
  };

  if (isLoading && !pairingCode) {
    return (
      <div className="bg-white p-6 rounded-lg border shadow-sm mb-6">
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg border shadow-sm mb-6">
      <h2 className="text-xl font-semibold mb-4 flex items-center">
        <Send className="mr-2" />
        Telegram Connection
      </h2>
      
      {isLoading ? (
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : showUpgrade ? (
        <div className="text-center">
          <p className="text-gray-600 mb-4">
            Telegram integration is available with our Medium or Premium plans. Upgrade to connect your Telegram account and manage your contacts on the go.
          </p>
          <button
            onClick={() => setShowUpgradeModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Upgrade Plan
          </button>
        </div>
      ) : isConnected ? (
        <div className="mt-4 p-3 bg-green-50 text-green-700 rounded-lg border border-green-200 flex items-center">
          <CheckCircle size={20} className="mr-2" />
          <div>
            <div>Your Inner Circle account is connected to Telegram</div>
            <div className="text-sm mt-1">
              Bot: <a 
                href="https://t.me/icCRMBot" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                @icCRMBot
              </a>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="mt-4 p-3 bg-amber-50 text-amber-700 rounded-lg border border-amber-200 flex items-center">
            <XCircle size={20} className="mr-2" />
            Your Inner Circle account is not connected to Telegram
          </div>
          <div className="flex justify-center mt-4">
            <button
              onClick={generatePairingCode}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              disabled={isGeneratingCode}
            >
              Connect Telegram
            </button>
          </div>
          {pairingCode && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-medium text-blue-800 mb-2">Follow these steps to connect:</h3>
              <ol className="list-decimal pl-5 text-gray-700 mb-4">
                <li>Open Telegram and search for @icCRMBot</li>
                <li>Start a chat with the bot</li>
                <li>Send the following pairing code to the bot:</li>
              </ol>
              <div className="flex items-center justify-between bg-white p-3 rounded border mb-4">
                <code className="font-mono text-lg">{pairingCode}</code>
                <button 
                  onClick={copyPairingCode}
                  className="text-blue-600 hover:text-blue-800 flex items-center"
                >
                  <Copy size={16} className="mr-1" />
                  {codeCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <div className="flex justify-center">
                <button
                  onClick={openTelegramBot}
                  className="flex items-center bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                >
                  <ExternalLink size={16} className="mr-2" />
                  Open Telegram Bot
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      <div className="flex justify-end">
        {isConnected && (
          <button
            onClick={disconnectTelegram}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            disabled={isLoading}
          >
            Disconnect Telegram
          </button>
        )}
      </div>
    </div>
  );
};

export default TelegramConnection;